import { useQuery } from '@tanstack/react-query';

import { UsageError } from '@/features/workspace-usage';
import { readLlmProfilesAdminOverview } from '@/lib/api/generated/api';

import { llmProfileKeys } from '../queryKeys';
import { splitOverviewItems } from '../llmProfileRows';
import type { LlmProfileAdminOverview } from '../types';

/**
 * Every LLM profile with its connection, spend and usage — the admin grid's
 * single read (spec 136 FR-008).
 *
 * The only place the admin grid's read touches the API client (research R2) —
 * this feature's mutations and its other reads call the generated client
 * themselves. The `queryFn` calls the generated `readLlmProfilesAdminOverview`
 * directly and narrows its response union on `status`, so there is no
 * hand-written client module, no manual `fetch` and no cast to write or later
 * delete.
 *
 * Anything but a 200 throws rather than resolving to an empty list, the same
 * shape as `useLlmProfiles` and for the same FR-007 reason: "this organization
 * has no profiles" and "we could not find out" must not render identically, and
 * a fallback to `[]` would make them indistinguishable. `retry: false` so a
 * refusal (403 for a non-admin) surfaces at once instead of being retried three
 * times on its way to the same answer.
 *
 * The thrown error is `workspace-usage`'s `UsageError`, which the
 * organization-admin hooks also use (`useOrganizationUsage` and
 * `useWorkflowLimits` throw the same one), so the
 * section can tell "you may not see this" from "we could not find out" and from
 * "you are no longer signed in" by `kind` rather than by matching message text —
 * which is what lets the load-failed state carry the 403 wording the UI contract
 * asks for. `reportAndBuildError` is where the three are chosen, and the only
 * place the status and the envelope's code are written down (R-F35).
 *
 * The **envelope** is what is cached and `select` unwraps `items`, because
 * `useSetDefaultLlmProfile`'s optimistic swap (T024) writes this key as
 * `{ items: [...] }` — one shape in the cache, one unwrapping at the edge.
 *
 * The unwrapping also splits off the deleted profiles that still have usage (spec
 * 258): `items` stays the live profiles every control and count reads, and
 * `deletedItems` is only ever shown.
 */
export function useLlmProfileRows() {
  const query = useQuery({
    queryKey: llmProfileKeys.adminOverview(),
    queryFn: async (): Promise<LlmProfileAdminOverview> => {
      const response = await readLlmProfilesAdminOverview();
      if (response.status === 200) return response.data;
      throw reportAndBuildError(response.status, response.data);
    },
    select: splitOverviewItems,
    retry: false,
  });

  return {
    items: query.data?.live,
    deletedItems: query.data?.deleted,
    isPending: query.isPending,
    loadFailed: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * The failure, as both a log line and the `UsageError` the section renders (R-F35/R-F36).
 *
 * One function for both because the two must not drift: the status and the envelope's
 * code are what a bug report needs and what the sentence on screen deliberately omits,
 * so the only place they are ever written down is the log.
 *
 * Three kinds, not one. `auth-expired` is separated out because "Try again" is the wrong
 * instruction for a 401 — the session is gone and the answer is to reload, which signs
 * in again (`RequireAuth`). A 429 and a 500 still share the `unavailable` wording, which
 * is honest: a reader can do the same thing about either. What they no longer share is
 * the log line.
 */
function reportAndBuildError(status: number, body: unknown): UsageError {
  const code = errorCode(body);
  console.error(
    `Could not read the LLM profiles admin overview: HTTP ${String(status)}${
      code === undefined ? '' : ` (${code})`
    }`,
  );

  if (status === 403) {
    return new UsageError(
      'forbidden',
      'Only an organization administrator can see LLM profiles.',
      { status, code },
    );
  }
  if (status === 401) {
    return new UsageError(
      'auth-expired',
      'Your session has expired. Reload the page to sign in again.',
      { status, code },
    );
  }
  return new UsageError(
    'unavailable',
    'The LLM profiles could not be loaded.',
    { status, code },
  );
}

/**
 * The envelope's `error`/`code`, when the body is one.
 *
 * A type guard rather than a cast (constitution §5, and this feature budgets no `as`):
 * the non-200 bodies are typed on the generated union, but a 500 is not in that union
 * at all, so the value genuinely is `unknown` here. `errors.ts`'s `toLlmProfileError`
 * does the same narrowing for the mutation paths.
 */
function errorCode(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  if ('error' in body && typeof body.error === 'string') return body.error;
  if ('code' in body && typeof body.code === 'string') return body.code;
  return undefined;
}
